import { Component, computed, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ItemMasterRequestService } from '../../../proxies/item/item-master-request.service';
import { ItemMasterRequest } from '../../../models/code-creation/code-creation';
import { SearchItemMasterRequest } from '../../../models/code-creation/search';
import _ from 'lodash';
import { Department } from '../../../models/department/deparment';
import { InventoryType } from '../../../models/inventory/inventory-type';
import { AssetType } from '../../../models/asset/assetType';
import { forkJoin, finalize } from 'rxjs';
import { DepartmentProxyService } from '../../../proxies/user-master/department-proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { InventoryTypeProxyService } from '../../../proxies/inventory/inventory-type.proxy.service';
import { AssetTypeProxyService } from '../../../proxies/asset/asset-type.proxy.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { EnumService } from '../../../services/enum.service';
import { EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { DataMasterAdminGroup, DataMasterViewerGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-codes-creations',
  templateUrl: './codes-creations.component.html',
  styleUrl: './codes-creations.component.scss',
})
export class CodesCreationsComponent implements OnInit {
  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  private lastSearchCriteria: SearchItemMasterRequest = { inventoryTypeReference: '', description: '' };

  isLoading = signal(0);
  itemMasterRequest = signal<ItemMasterRequest[]>([]);
  assetTypeOptions = signal<AssetType[]>([]);
  inventoryTypeOptions = signal<InventoryType[]>([]);
  departmentOptions = signal<Department[]>([]);
  uom = signal<EnumValues>([]);
  financeOptions = signal<{ label: string; value: number }[]>([]);

  canEdit = computed(() => {
    if (AuthService.hasRole(DataMasterViewerGroup.ItemCodeRequestViewer)) {
      return true;
    }
    if (AuthService.hasRole(DataMasterAdminGroup.ItemMasterAdmin)) {
      return true;
    }
    return false;
  });

  constructor(
    private router: Router,
    private itemMasterRequestService: ItemMasterRequestService,
    private assetTypeProxyService: AssetTypeProxyService,
    private inventoryTypeProxyService: InventoryTypeProxyService,
    private businessProxyService: BusinessProxyService,
    private departmentProxyService: DepartmentProxyService,
    public localHelperService: LocaleHelperService,
    private enumService: EnumService,
  ) {
    const values = this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values;
    this.uom.set(values);
    this.getBusinessEnums();
  }

  ngOnInit() {
    this.getInitialData();
  }

  private getBusinessEnums() {
    if (_.isNil(localStorage.getItem('financeEnums'))) return;

    const rawData = JSON.parse(localStorage.getItem('financeEnums')!) as {
      name: string;
      values: Record<string, string>;
    }[];

    const mappedData = Object.entries(rawData[2].values).map(([key, val]) => {
      return {
        label: val,
        value: Number(key),
      };
    });

    // console.log(mappedData);
    this.financeOptions.set(mappedData);
  }

  private getInitialData() {
    this.isLoading.update((val) => val + 1);

    const inventoryType$ = this.inventoryTypeProxyService.getAllInventoryType();
    const assetType$ = this.assetTypeProxyService.getAll();
    const department$ = this.departmentProxyService.getAll();

    forkJoin([inventoryType$, assetType$, department$])
      .pipe(finalize(() => this.isLoading.update((val) => val - 1)))
      .subscribe(([inventoryType, assetType, department]) => {
        if (!_.isNil(inventoryType.data)) {
          this.inventoryTypeOptions.set(inventoryType.data);
        }

        if (!_.isNil(assetType.data)) {
          this.assetTypeOptions.set(assetType.data);
        }

        if (!_.isNil(department.data)) {
          this.departmentOptions.set(department.data);
        }

        this.onSearch(this.lastSearchCriteria);
      });
  }

  onSearch(searchCriteria: SearchItemMasterRequest): void {
    this.lastSearchCriteria = searchCriteria;
    this.isLoading.update((val) => val + 1);

    this.itemMasterRequestService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.isLoading.update((val) => val - 1)))
      .subscribe({
        next: (response) => {
          this.itemMasterRequest.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: (error) => {
          console.error(error);
          this.itemMasterRequest.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  newCodeCreationRequest(param: string) {
    this.router.navigate(['/code-creation-detail'], {
      queryParams: { id: param },
    });
  }

  safeUndefinedData(data: string | number) {
    return data ? data : '-----';
  }

  getDepartment(id: string) {
    const match = this.departmentOptions().find((x) => x.id === id)?.name;

    return this.safeUndefinedData(this.localHelperService.getLocalizedValue(match!));
  }

  getInventoryType(id: string) {
    const match = this.inventoryTypeOptions().find((x) => x.id === id)?.name;

    return this.safeUndefinedData(this.localHelperService.getLocalizedValue(match!));
  }

  getAssetType(id: string) {
    const match = this.assetTypeOptions().find((x) => x.id === id)?.name;
    return this.safeUndefinedData(match!);
    // return this.localHelperService.getLocalizedValue(match!);
  }
  getUom(id: number) {
    const match = this.uom()[id];
    return this.safeUndefinedData(match!);
  }
}
